/**
 * Shared Sentry scrubbing / environment helpers (web client + typed imports).
 * Keep PII out of events — especially auth, Stripe, and request bodies.
 */

const SENSITIVE_HEADER = /^(authorization|cookie|set-cookie|x-api-key|x-supabase-auth)$/i;
const SENSITIVE_KEY =
  /(password|passwd|secret|token|authorization|api[_-]?key|credit[_-]?card|card[_-]?number|cvc|cvv|iban|stripe|receipt)/i;

export function resolveSentryEnvironment(): string {
  const explicit = import.meta.env.VITE_SENTRY_ENVIRONMENT;
  if (typeof explicit === "string" && explicit.trim()) return explicit.trim();

  const appUrl = import.meta.env.VITE_PUBLIC_APP_URL ?? "";
  if (/te2|staging|preview/i.test(appUrl)) return "te2";

  if (import.meta.env.PROD) return "production";
  return "development";
}

function scrubValue(key: string, value: unknown): unknown {
  if (SENSITIVE_KEY.test(key)) return "[Filtered]";
  if (typeof value === "string" && value.length > 500) return `${value.slice(0, 500)}…[truncated]`;
  return value;
}

function scrubRecord(input: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!input) return input;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      out[key] = scrubRecord(value as Record<string, unknown>);
    } else {
      out[key] = scrubValue(key, value);
    }
  }
  return out;
}

/** Drop auth headers / bodies and scrub sensitive extras before send. */
export function scrubSentryEvent<T extends Record<string, unknown>>(event: T): T | null {
  const next = { ...event } as T & {
    request?: { headers?: Record<string, string>; data?: unknown; cookies?: unknown };
    user?: { email?: string; ip_address?: string; username?: string; id?: string };
    extra?: Record<string, unknown>;
    contexts?: Record<string, unknown>;
  };

  if (next.request) {
    const headers = next.request.headers;
    if (headers) {
      const cleaned: Record<string, string> = {};
      for (const [k, v] of Object.entries(headers)) {
        cleaned[k] = SENSITIVE_HEADER.test(k) ? "[Filtered]" : v;
      }
      next.request.headers = cleaned;
    }
    // Never ship request bodies (login, Stripe checkout, admin proxies).
    delete next.request.data;
    delete next.request.cookies;
  }

  if (next.user) {
    // Keep opaque id if present; drop email / IP / username.
    next.user = next.user.id ? { id: next.user.id } : undefined;
  }

  if (next.extra) next.extra = scrubRecord(next.extra);
  if (next.contexts) next.contexts = scrubRecord(next.contexts) as typeof next.contexts;

  return next;
}
